import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft, Building2, X, FileText,
  MapPin, Hash, MessageSquare,
  Send, CheckCircle, AlertCircle,
  Truck, Package, ShieldCheck, Tag, Layers, ListPlus
} from "lucide-react";
import { productAPI, quoteAPI, BASE_URL } from "../services/api";
import { useQuoteCart } from "../context";

const getImage = (url, fallback = "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=600&q=80") => {
  if (!url) return fallback;
  if (url.startsWith("http")) return url;
  return `${BASE_URL}${url}`;
};

const errorCls = "mt-1 text-xs text-red-500";

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandFilter = searchParams.get("brand");
  const brandIdParam = searchParams.get("brandId");

  const { addToQuoteCart } = useQuoteCart();
  const [product, setProduct] = useState(null);
  const [allBrandsData, setAllBrandsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteCartBanner, setQuoteCartBanner] = useState(false);
  const [simpleQty, setSimpleQty] = useState(1);

  // Row selection: key = "brandId-sizeId"
  const [selected, setSelected] = useState({});      // { key: { brandId, brandName, brandType, sizeId, size, height, width, quantity } }
  const [rowQty, setRowQty] = useState({});           // { key: number } — editable qty per row
  const [rowDims, setRowDims] = useState({});

  // Quote modal
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [modalBrandType, setModalBrandType] = useState(null);      // 'primary' | 'secondary' | null
  const [modalSelectedBrands, setModalSelectedBrands] = useState([]); // array of brand objects
  const [quoteForm, setQuoteForm] = useState({ notes: "", address: "", pincode: "" });
  const [quoteErrors, setQuoteErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => { fetchProduct(); }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const [productRes, brandsRes] = await Promise.all([
        productAPI.getProductById(id),
        productAPI.getProductBrands(id)
      ]);
      if (productRes.data.success) setProduct(productRes.data.data);
      if (brandsRes.data.success) {
        const { primary = [], secondary = [], other = [] } = brandsRes.data.data;
        setAllBrandsData([...primary, ...secondary, ...other]);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#f5f5f0] pt-40 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-600" />
    </div>
  );

  if (!product) return (
    <div className="min-h-screen bg-[#f5f5f0] pt-40 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-primary-800 mb-4">Product not found</h2>
        <button onClick={() => navigate("/products")} className="px-5 py-2 bg-secondary-600 text-white rounded-lg">Back to Products</button>
      </div>
    </div>
  );

  const allBrands = allBrandsData;
  const hasPrimarySecondary = allBrands.some(b => b.brand_type === 'primary' || b.brand_type === 'secondary');

  // Deduplicate sizes by name — same size may appear under multiple brands
  const seenSizes = new Set();
  const allRows = (product.sizes || []).reduce((acc, sz) => {
    if (seenSizes.has(sz.size)) return acc;
    seenSizes.add(sz.size);
    acc.push({
      key: sz.size,
      sizeId: sz.id,
      size: sz.size,
      height: sz.height,
      width: sz.width,
      stock: sz.quantity
    });
    return acc;
  }, []);

  const getQty = (key) => rowQty[key] !== undefined ? rowQty[key] : 0;
  const getDim = (key, field, def) => rowDims[key]?.[field] !== undefined ? rowDims[key][field] : (def ?? '');

  const toggleRow = (row) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[row.key]) {
        delete next[row.key];
      } else {
        next[row.key] = { ...row, quantity: getQty(row.key) };
      }
      return next;
    });
  };

  const updateQty = (key, val) => {
    const qty = Math.max(0, parseInt(val) || 0);
    setRowQty(prev => ({ ...prev, [key]: qty }));
    if (selected[key]) {
      setSelected(prev => ({ ...prev, [key]: { ...prev[key], quantity: qty } }));
    }
  };

  const updateDim = (key, field, val) => {
    setRowDims(prev => ({ ...prev, [key]: { ...prev[key], [field]: val } }));
  };

  const toggleAll = () => {
    if (Object.keys(selected).length === allRows.length) {
      setSelected({});
    } else {
      const all = {};
      allRows.forEach(row => { all[row.key] = { ...row, quantity: getQty(row.key) }; });
      setSelected(all);
    }
  };

  const selectedList = Object.values(selected);
  const selectedCount = selectedList.length;
  const allChecked = allRows.length > 0 && selectedCount === allRows.length;

  const isAuthenticated = () => {
    const token = localStorage.getItem("token");
    const userStr = localStorage.getItem("user");
    if (!token || !userStr) return false;
    try { return JSON.parse(userStr).user_type === "customer"; } catch { return false; }
  };

  const openQuoteModal = () => {
    if (!isAuthenticated()) { navigate(`/login?redirect=/products/${id}`); return; }
    if (selectedCount === 0) return;
    setModalBrandType(null);
    setModalSelectedBrands([]);
    setQuoteForm({ notes: "", address: "", pincode: "" });
    setQuoteErrors({});
    setAuthError("");
    setQuoteSuccess(false);
    setShowQuoteModal(true);
  };

  // Sizeless products have no brand/size table at all — quantity-only, no brand to select.
  const handleAddToQuoteCart = () => {
    if (!isAuthenticated()) { navigate(`/login?redirect=/products/${id}`); return; }

    addToQuoteCart({
      product_id: product.id,
      product_name: product.name,
      product_image: product.image_url,
      unit: product.measurement?.symbol || product.unit || null,
      size_id: null,
      size: null,
      quantity: parseFloat(simpleQty) || 1
    });

    setQuoteCartBanner(true);
    setTimeout(() => setQuoteCartBanner(false), 3000);
  };

  // Sized products go through the quote modal so a brand is always attached — requires
  // exactly one brand selected (or none, if this product has no brand entities at all)
  // so every quote-cart line has an unambiguous brand for the admin to price against.
  const handleAddToQuoteCartFromModal = () => {
    if (modalAvailableBrands.length > 0 && modalSelectedBrands.length !== 1) return;
    const brand = modalSelectedBrands[0] || null;

    addToQuoteCart(selectedList.map(item => ({
      product_id: product.id,
      product_name: product.name,
      product_image: product.image_url,
      unit: product.measurement?.symbol || product.unit || null,
      brand_id: brand?.id || null,
      brand_name: brand?.brand_name || null,
      brand_type: brand?.brand_type || null,
      size_id: item.sizeId,
      size: item.size,
      height: getDim(item.key, 'height', item.height) ? parseFloat(getDim(item.key, 'height', item.height)) : null,
      width: getDim(item.key, 'width', item.width) ? parseFloat(getDim(item.key, 'width', item.width)) : null,
      quantity: parseFloat(item.quantity) || 1
    })));

    setShowQuoteModal(false);
    setQuoteCartBanner(true);
    setTimeout(() => setQuoteCartBanner(false), 3000);
  };

  // Brands shown in modal — filtered by type if primary/secondary
  const modalAvailableBrands = hasPrimarySecondary
    ? allBrands.filter(b => b.brand_type === modalBrandType)
    : allBrands;

  const toggleModalBrand = (brand) => {
    setModalSelectedBrands(prev => {
      const exists = prev.find(b => b.id === brand.id);
      return exists ? prev.filter(b => b.id !== brand.id) : [...prev, brand];
    });
    setQuoteErrors(e => { const n = { ...e }; delete n.brand; return n; });
  };

  const validateQuote = () => {
    const errs = {};
    if (hasPrimarySecondary && !modalBrandType) errs.brandType = "Please select Primary or Secondary brand";
    if (modalSelectedBrands.length === 0) errs.brand = "Please select at least one brand";
    if (!quoteForm.notes.trim()) errs.notes = "Additional requirements are required";
    if (!quoteForm.address.trim()) errs.address = "Delivery address is required";
    if (!quoteForm.pincode.trim()) errs.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(quoteForm.pincode.trim())) errs.pincode = "Enter a valid 6-digit pincode";
    return errs;
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    const errs = validateQuote();
    if (Object.keys(errs).length > 0) { setQuoteErrors(errs); return; }
    setSubmitting(true);
    try {
      await quoteAPI.createQuote({
        product_id: parseInt(id),
        quantity: selectedList.reduce((sum, i) => sum + (parseFloat(i.quantity) || 1), 0),
        unit: product.measurement?.symbol || "ton",
        customer_notes: quoteForm.notes,
        delivery_address: quoteForm.address,
        pincode: quoteForm.pincode,
        selected_brands: modalSelectedBrands.map(b => ({
          id: b.id,
          brand_name: b.brand_name,
          brand_type: b.brand_type || null
        })),
        selected_items: selectedList.map(i => ({
          sizeId: i.sizeId,
          size: i.size,
          quantity: parseFloat(i.quantity) || 1,
          height: getDim(i.key, 'height', i.height) ? parseFloat(getDim(i.key, 'height', i.height)) : null,
          width: getDim(i.key, 'width', i.width) ? parseFloat(getDim(i.key, 'width', i.width)) : null
        }))
      });
      setQuoteSuccess(true);
      setTimeout(() => {
        setShowQuoteModal(false);
        setQuoteSuccess(false);
        setSelected({});
        navigate("/dashboard?tab=quotes");
      }, 2000);
    } catch (err) {
      setAuthError(err.response?.data?.message || "Failed to submit quote");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f0] pt-36 pb-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Back */}
        <button
          onClick={() => brandIdParam ? navigate(`/brands/${brandIdParam}`) : navigate("/products")}
          className="flex items-center gap-2 text-sm text-primary-400 hover:text-secondary-600 mb-5 transition-colors font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          {brandIdParam ? `Back to ${brandFilter || 'Brand'}` : 'Back to Products'}
        </button>

        {/* Product Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-primary-100 p-6 mb-5">
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            {/* Image */}
            <div className="w-36 h-36 flex-shrink-0 rounded-xl overflow-hidden bg-secondary-50 border border-primary-100">
              <img src={getImage(product.image_url)} alt={product.name}
                className="w-full h-full object-cover"
                onError={e => { e.target.src = "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=200"; }} />
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {product.category && (
                  <span className="text-xs px-2.5 py-0.5 bg-primary-50 text-primary-600 rounded-full font-medium flex items-center gap-1">
                    <Tag className="w-3 h-3" /> {product.category.name}
                  </span>
                )}
                {product.type && (
                  <span className="text-xs px-2.5 py-0.5 bg-secondary-50 text-secondary-700 rounded-full border border-secondary-200 font-medium flex items-center gap-1">
                    <Layers className="w-3 h-3" /> {product.type.name}
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-extrabold text-primary-800 mb-2">{product.name}</h1>
              {product.description && (
                <p className="text-sm text-primary-500 mb-3 leading-relaxed max-w-2xl">{product.description}</p>
              )}
              <div className="flex flex-wrap gap-4 text-sm">
                {product.measurement && (
                  <span className="text-primary-400">Unit: <strong className="text-primary-700">{product.measurement.name} ({product.measurement.symbol})</strong></span>
                )}
                {product.sku && (
                  <span className="text-primary-400">SKU: <strong className="text-primary-700">{product.sku}</strong></span>
                )}
                <span className={`font-semibold ${product.is_active ? 'text-green-600' : 'text-red-500'}`}>
                  {product.is_active ? 'Available' : 'Unavailable'}
                </span>
              </div>
            </div>

            {/* CTA */}
            <div className="flex-shrink-0 flex flex-col items-center gap-2">
              {allRows.length === 0 && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    value={simpleQty}
                    onChange={e => setSimpleQty(e.target.value)}
                    className="w-20 px-2.5 py-2 text-sm border border-primary-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-secondary-400"
                  />
                  <span className="text-xs text-primary-400">{product.measurement?.symbol || product.unit || 'unit'}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={openQuoteModal}
                  disabled={!product.is_active || selectedCount === 0}
                  className="flex items-center gap-2 px-5 py-3 bg-secondary-600 text-white rounded-xl font-semibold hover:bg-secondary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm whitespace-nowrap"
                >
                  <FileText className="w-4 h-4" />
                  Get Quote {selectedCount > 0 && `(${selectedCount})`}
                </button>
                <button
                  onClick={allRows.length > 0 ? openQuoteModal : handleAddToQuoteCart}
                  disabled={!product.is_active || (allRows.length > 0 && selectedCount === 0)}
                  title={allRows.length > 0
                    ? "Choose a brand for the selected sizes, then add to your quote cart"
                    : "Add this product to your quote cart to request a quote together with other products"}
                  className="flex items-center gap-2 px-5 py-3 border-2 border-secondary-600 text-secondary-600 rounded-xl font-semibold hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  <ListPlus className="w-4 h-4" />
                  Add to Quote Cart
                </button>
              </div>
              <p className="text-xs text-primary-400">
                {allRows.length === 0
                  ? '"Get Quote" submits now • "Add to Quote Cart" queues with other products'
                  : selectedCount === 0 ? 'Select items below' : `${selectedCount} item${selectedCount !== 1 ? 's' : ''} selected — pick a brand next`}
              </p>
            </div>
          </div>
        </div>

        {/* Added to Quote Cart banner */}
        {quoteCartBanner && (
          <div className="fixed bottom-6 right-6 z-50 bg-white border border-secondary-200 shadow-xl rounded-xl px-5 py-4 flex items-center gap-3 animate-slide-up">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-primary-800">Added to Quote Cart</p>
              <Link to="/quote-cart" className="text-xs text-secondary-600 hover:underline font-medium">
                View Quote Cart →
              </Link>
            </div>
          </div>
        )}

        {/* ─── Flat Sizes Table ─── */}
        {allRows.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-primary-100 overflow-hidden mb-5">
            {/* Table header */}
            <div className="flex items-center justify-between px-5 py-3.5 bg-primary-700">
              <h2 className="text-sm font-bold text-white flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Available Sizes
              </h2>
              {selectedCount > 0 && (
                <span className="text-xs bg-white/20 text-white px-2.5 py-1 rounded-full font-medium">
                  {selectedCount} of {allRows.length} selected
                </span>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-primary-50 border-b border-primary-100">
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={toggleAll}
                        className="w-4 h-4 cursor-pointer accent-secondary-600"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-primary-500 uppercase tracking-wide">Size</th>
                    {product.is_flat && (
                      <>
                        <th className="text-left px-4 py-3 text-xs font-bold text-primary-500 uppercase tracking-wide">Height (mm)</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-primary-500 uppercase tracking-wide">Width (mm)</th>
                      </>
                    )}
                    <th className="text-left px-4 py-3 text-xs font-bold text-primary-500 uppercase tracking-wide">
                      Req. Qty {product.measurement?.symbol ? `(${product.measurement.symbol})` : ''}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-primary-50">
                  {allRows.map(row => {
                    const isChecked = !!selected[row.key];
                    const qty = getQty(row.key);
                    return (
                      <tr
                        key={row.key}
                        onClick={() => toggleRow(row)}
                        className={`cursor-pointer transition-colors ${isChecked ? 'bg-secondary-50/70' : 'hover:bg-primary-50/40'}`}
                      >
                        <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleRow(row)}
                            className="w-4 h-4 cursor-pointer accent-secondary-600"
                          />
                        </td>
                        <td className="px-4 py-3 font-semibold text-primary-800">{row.size}</td>
                        {product.is_flat && (
                          <>
                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                              <input
                                type="number"
                                min="0"
                                placeholder="—"
                                value={getDim(row.key, 'height', row.height)}
                                onChange={e => updateDim(row.key, 'height', e.target.value)}
                                className="w-24 px-2.5 py-1.5 text-sm border border-amber-200 rounded-lg bg-amber-50 text-primary-800 font-medium focus:outline-none focus:ring-2 focus:ring-amber-400"
                              />
                            </td>
                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                              <input
                                type="number"
                                min="0"
                                placeholder="—"
                                value={getDim(row.key, 'width', row.width)}
                                onChange={e => updateDim(row.key, 'width', e.target.value)}
                                className="w-24 px-2.5 py-1.5 text-sm border border-amber-200 rounded-lg bg-amber-50 text-primary-800 font-medium focus:outline-none focus:ring-2 focus:ring-amber-400"
                              />
                            </td>
                          </>
                        )}
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <input
                            type="number"
                            min="0"
                            value={qty}
                            onChange={e => updateQty(row.key, e.target.value)}
                            className="w-24 px-2.5 py-1.5 text-sm border border-primary-200 rounded-lg bg-white text-primary-800 font-medium focus:outline-none focus:ring-2 focus:ring-secondary-400"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer bar when items selected */}
            {selectedCount > 0 && (
              <div className="flex items-center justify-between px-5 py-3 bg-secondary-50 border-t border-secondary-200">
                <span className="text-sm text-secondary-800 font-medium">
                  {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center gap-3">
                  <button onClick={() => setSelected({})} className="text-xs text-secondary-600 hover:underline">Clear</button>
                  <button
                    onClick={openQuoteModal}
                    className="flex items-center gap-1.5 px-4 py-2 border-2 border-secondary-600 text-secondary-600 text-sm rounded-lg font-semibold hover:bg-secondary-50 transition-colors"
                  >
                    <ListPlus className="w-3.5 h-3.5" /> Add to Quote Cart
                  </button>
                  <button
                    onClick={openQuoteModal}
                    className="flex items-center gap-1.5 px-4 py-2 bg-secondary-600 text-white text-sm rounded-lg font-semibold hover:bg-secondary-700 transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" /> Get Quote
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Feature badges */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: Truck, label: "Fast Delivery", sub: "3-7 business days", color: "text-blue-600", bg: "bg-blue-50" },
            { icon: ShieldCheck, label: "Quality Assured", sub: "100% certified", color: "text-green-600", bg: "bg-green-50" },
            { icon: Package, label: "Bulk Orders", sub: "Min. order supported", color: "text-secondary-600", bg: "bg-secondary-50" },
          ].map(f => (
            <div key={f.label} className="bg-white rounded-xl border border-primary-100 p-3 flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg ${f.bg} flex items-center justify-center flex-shrink-0`}>
                <f.icon className={`w-4 h-4 ${f.color}`} />
              </div>
              <div>
                <p className="text-xs font-bold text-primary-700">{f.label}</p>
                <p className="text-xs text-primary-400">{f.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Quote Modal ─── */}
      {showQuoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !submitting && setShowQuoteModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">

            {/* Modal Header */}
            <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-primary-100 rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-secondary-600" />
                <h3 className="text-lg font-bold text-primary-800">Request Quote</h3>
                <span className="text-xs text-primary-400 hidden sm:block">— {product.name}</span>
              </div>
              {!submitting && (
                <button onClick={() => setShowQuoteModal(false)} className="p-1.5 hover:bg-primary-50 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-primary-400" />
                </button>
              )}
            </div>

            <div className="p-6 space-y-5">
              {quoteSuccess ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-primary-800 mb-1">Quote Submitted!</h3>
                  <p className="text-primary-400 text-sm">Redirecting to your quotes...</p>
                </div>
              ) : (
                <>
                  {authError && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {authError}
                    </div>
                  )}

                  {/* Step 1: Brand Type (only if primary/secondary exist) */}
                  {hasPrimarySecondary && (
                    <div>
                      <p className="text-sm font-bold text-primary-700 mb-2.5">
                        Select Brand Type <span className="text-red-500">*</span>
                      </p>
                      <div className="flex gap-3">
                        {['primary', 'secondary'].map(type => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => {
                              setModalBrandType(type);
                              setModalSelectedBrands([]);
                              setQuoteErrors(e => { const n = { ...e }; delete n.brandType; delete n.brand; return n; });
                            }}
                            className={`flex-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all flex flex-col items-center gap-1 ${
                              modalBrandType === type
                                ? type === 'primary'
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-purple-500 bg-purple-50 text-purple-700'
                                : 'border-gray-200 text-primary-600 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <span className="text-lg">{type === 'primary' ? '🏆' : '🥈'}</span>
                            {type === 'primary' ? 'Primary Brand' : 'Secondary Brand'}
                          </button>
                        ))}
                      </div>
                      {quoteErrors.brandType && <p className="mt-1.5 text-xs text-red-500">{quoteErrors.brandType}</p>}
                    </div>
                  )}

                  {/* Step 2: Brand selection (checkbox list) */}
                  {(!hasPrimarySecondary || modalBrandType) && (
                    <div>
                      <p className="text-sm font-bold text-primary-700 mb-2">
                        Select Brand(s) <span className="text-red-500">*</span>
                        {modalSelectedBrands.length > 0 && (
                          <span className="ml-2 text-xs font-normal text-secondary-600">({modalSelectedBrands.length} selected)</span>
                        )}
                      </p>
                      <div className="space-y-2">
                        {modalAvailableBrands.map(brand => {
                          const isSelected = modalSelectedBrands.some(b => b.id === brand.id);
                          const logoUrl = brand.logo_url
                            ? (brand.logo_url.startsWith('http') ? brand.logo_url : `${BASE_URL}${brand.logo_url}`)
                            : null;
                          return (
                            <label
                              key={brand.id}
                              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-secondary-500 bg-secondary-50'
                                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleModalBrand(brand)}
                                className="w-4 h-4 accent-secondary-600 flex-shrink-0"
                              />
                              {logoUrl ? (
                                <img src={logoUrl} alt={brand.brand_name}
                                  className="w-8 h-8 rounded-lg object-contain bg-white border border-gray-100 p-0.5 flex-shrink-0" />
                              ) : (
                                <div className="w-8 h-8 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Building2 className="w-4 h-4 text-secondary-500" />
                                </div>
                              )}
                              <span className={`text-sm font-semibold ${isSelected ? 'text-secondary-800' : 'text-primary-700'}`}>
                                {brand.brand_name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {quoteErrors.brand && <p className="mt-1.5 text-xs text-red-500">{quoteErrors.brand}</p>}
                    </div>
                  )}

                  {/* Step 3: Selected items summary (shown after at least one brand selected) */}
                  {modalSelectedBrands.length > 0 && (
                    <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4">
                      <h4 className="text-sm font-bold text-secondary-800 mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4" /> Selected Items ({selectedCount})
                        <span className="text-xs font-normal text-secondary-600">
                          — Brands: {modalSelectedBrands.map(b => b.brand_name).join(', ')}
                        </span>
                      </h4>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                        {selectedList.map((item, i) => (
                          <div key={i} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2 border border-secondary-100">
                            <span className="font-semibold text-primary-800">{item.size}</span>
                            <div className="flex items-center gap-2 text-primary-500">
                              {product.is_flat && (
                                <span className="text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">
                                  {getDim(item.key, 'height', item.height) || '—'}×{getDim(item.key, 'width', item.width) || '—'}
                                </span>
                              )}
                              <span>Qty: <strong className="text-primary-700">{item.quantity}</strong></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quick path: queue with other products instead of submitting this one alone.
                      Needs exactly one brand chosen so the admin sees an unambiguous line item. */}
                  {selectedCount > 0 && (modalAvailableBrands.length === 0 || modalSelectedBrands.length > 0) && (
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={handleAddToQuoteCartFromModal}
                        disabled={modalAvailableBrands.length > 0 && modalSelectedBrands.length !== 1}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-secondary-600 text-secondary-600 rounded-xl text-sm font-bold hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ListPlus className="w-4 h-4" /> Add to Quote Cart
                      </button>
                      {modalAvailableBrands.length > 0 && modalSelectedBrands.length > 1 && (
                        <p className="text-xs text-amber-600 text-center">
                          Select exactly one brand to add these items to your Quote Cart — add again for another brand.
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-primary-300 font-semibold">
                        <div className="flex-1 h-px bg-primary-100" /> OR SUBMIT THIS QUOTE NOW <div className="flex-1 h-px bg-primary-100" />
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleQuoteSubmit} className={`space-y-4 ${modalSelectedBrands.length === 0 ? 'hidden' : ''}`}>
                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-bold text-primary-700 mb-1.5">
                        Additional Requirements <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <MessageSquare className="absolute top-3 left-3 w-4 h-4 text-primary-300 pointer-events-none" />
                        <textarea rows={3} value={quoteForm.notes}
                          onChange={e => setQuoteForm(f => ({ ...f, notes: e.target.value }))}
                          placeholder="Delivery timeline, grade preferences, special requirements..."
                          disabled={submitting}
                          className={`w-full pl-9 pr-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400 resize-none ${quoteErrors.notes ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                        />
                      </div>
                      {quoteErrors.notes && <p className={errorCls}>{quoteErrors.notes}</p>}
                    </div>

                    {/* Address */}
                    <div>
                      <label className="block text-sm font-bold text-primary-700 mb-1.5">
                        Delivery Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute top-3 left-3 w-4 h-4 text-primary-300 pointer-events-none" />
                        <textarea rows={2} value={quoteForm.address}
                          onChange={e => setQuoteForm(f => ({ ...f, address: e.target.value }))}
                          placeholder="Full delivery address..."
                          disabled={submitting}
                          className={`w-full pl-9 pr-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400 resize-none ${quoteErrors.address ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                        />
                      </div>
                      {quoteErrors.address && <p className={errorCls}>{quoteErrors.address}</p>}
                    </div>

                    {/* Pincode */}
                    <div>
                      <label className="block text-sm font-bold text-primary-700 mb-1.5">
                        Pincode <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Hash className="absolute top-1/2 -translate-y-1/2 left-3 w-4 h-4 text-primary-300 pointer-events-none" />
                        <input type="text" maxLength={6} value={quoteForm.pincode}
                          onChange={e => setQuoteForm(f => ({ ...f, pincode: e.target.value.replace(/\D/g, '') }))}
                          placeholder="6-digit pincode"
                          disabled={submitting}
                          className={`w-full pl-9 pr-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400 ${quoteErrors.pincode ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                        />
                      </div>
                      {quoteErrors.pincode && <p className={errorCls}>{quoteErrors.pincode}</p>}
                    </div>

                    <div className="bg-primary-50 border border-primary-100 rounded-xl p-3 text-xs text-primary-500">
                      <strong className="text-primary-700">What happens next?</strong> Our team reviews your request and provides competitive pricing within 24–48 hours.
                    </div>

                    <div className="flex gap-3 pt-1">
                      <button type="button" onClick={() => setShowQuoteModal(false)} disabled={submitting}
                        className="flex-1 px-4 py-2.5 border border-primary-200 rounded-xl text-sm text-primary-600 hover:bg-primary-50 disabled:opacity-50 transition-colors">
                        Cancel
                      </button>
                      <button type="submit" disabled={submitting || modalSelectedBrands.length === 0}
                        className="flex-1 px-4 py-2.5 bg-secondary-600 text-white rounded-xl text-sm font-bold hover:bg-secondary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                        {submitting
                          ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Submitting...</>
                          : <><Send className="w-4 h-4" /> Submit Quote Now</>
                        }
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
